/**
 * Device detection utilities
 *
 * Provides capability-based device detection without user-agent sniffing where possible.
 * Safe to use from React and non-React code.
 */

export type DeviceInfo = {
  isMobile: boolean;
  isDesktop: boolean;
  hasTouch: boolean;
  platform: "ios" | "android" | "desktop" | "unknown";
  isInAppBrowser: boolean;
};

/**
 * Detect the device platform
 * @returns The detected platform (ios, android, desktop, or unknown)
 */
function detectPlatform(): "ios" | "android" | "desktop" | "unknown" {
  // Check if running in browser environment
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return "unknown";
  }

  const userAgent = navigator.userAgent.toLowerCase();

  // iOS detection: iPhone, iPad, or iPod
  if (/iphone|ipad|ipod/.test(userAgent)) {
    return "ios";
  }

  // Android detection
  if (/android/.test(userAgent)) {
    return "android";
  }

  // iPadOS 13+ in "Request Desktop Website" mode reports a macOS UA with no
  // iPad marker. Prefer maxTouchPoints when available, and fall back to the
  // iOS-style Mobile token under a Macintosh UA for WebViews that omit touch
  // metadata.
  if (/macintosh/.test(userAgent)) {
    const hasMultiTouch =
      typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
    const hasIosMobileToken = /mobile\/\w+/.test(userAgent);
    if (hasMultiTouch || hasIosMobileToken) {
      return "ios";
    }
  }

  // Desktop fallback
  return "desktop";
}

// UA markers for common social/messaging in-app browsers (WKWebView/WebView
// hosted inside another app). These hosts frequently break getUserMedia or
// scope camera permission to the host app rather than the site. Detection is
// intentionally decoupled from remediation — this module only answers "are
// we likely running under one of these hosts?" and lets the capture-error
// overlay decide what (if any) conditional hint to surface.
const IN_APP_BROWSER_MARKERS = [
  "instagram",
  "fban", // Facebook app (iOS/Android)
  "fbav", // Facebook app (iOS/Android)
  "fb_iab", // Facebook in-app browser
  "fbsn",
  "messenger",
  "gsa/", // Google Search App in-app browser (iOS)
  "googlewv", // Google app webview token
  "geminiios", // Gemini iOS in-app browser
  "musical_ly", // TikTok (older UA)
  "bytedancewebview", // TikTok (newer UA)
  "tiktok",
  "twitter",
  "linkedinapp",
  "snapchat",
  "pinterest",
  "line/",
];

function detectInAppBrowser(): boolean {
  if (typeof navigator === "undefined") return false;

  const userAgent = navigator.userAgent.toLowerCase();

  if (IN_APP_BROWSER_MARKERS.some((marker) => userAgent.includes(marker))) {
    return true;
  }

  // Generic Android WebView: UA contains "; wv)" somewhere in the Chrome token.
  // Example: "Mozilla/5.0 (Linux; Android 10; ...; wv) AppleWebKit/537.36 ..."
  if (/android.*;\s*wv\)/.test(userAgent)) {
    return true;
  }

  return false;
}

/**
 * Get device information including mobile/desktop detection, touch capability, and platform
 * @returns DeviceInfo object with device characteristics
 */
export function getDeviceInfo(): DeviceInfo {
  // Check if running in browser environment
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return {
      isMobile: false,
      isDesktop: true,
      hasTouch: false,
      platform: "unknown",
      isInAppBrowser: false,
    };
  }

  const hasUAData = "userAgentData" in navigator && (navigator as any).userAgentData !== undefined;

  const isMobile = hasUAData
    ? (navigator as any).userAgentData.mobile
    : window.matchMedia("(pointer: coarse)").matches;

  const hasTouch = "maxTouchPoints" in navigator && navigator.maxTouchPoints > 0;

  const platform = detectPlatform();
  const isInAppBrowser = detectInAppBrowser();

  return {
    isMobile,
    isDesktop: !isMobile,
    hasTouch,
    platform,
    isInAppBrowser,
  };
}
